import {
  CreateWorkspaceRequest,
  WorkspaceResult,
  WorkspaceService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  workspaceName: z.string().min(2, {
    message: 'Workspace name must be at least 2 characters.',
  }),
  workspaceDesc: z.string().optional(),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  organizationId: string;
}

export function NewWorkspaceDrawer({ open, setOpen, organizationId }: Props) {
  // query client
  const queryClient = useQueryClient();
  const workspaces = useStore($workspaces);

  // Define the form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspaceName: '',
      workspaceDesc: '',
    },
  });

  // mutation to create a new workspace
  const createWorkspace = useMutation({
    mutationFn: async (payload: CreateWorkspaceRequest) => {
      // call the api to create a new workspace
      const res = await WorkspaceService.createWorkspace(payload);
      return res;
    },
    onSuccess: data => {
      // invalidate queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getWorkspaces', organizationId],
      });

      const newWorkspace: WorkspaceResult = {
        workspace: data,
        invites: 0,
        collaborators: [],
        projects: [],
      };

      $workspaces.set([newWorkspace, ...workspaces]);

      // close the drawer and reset the form
      setOpen(false);
      form.reset();
    },
    onError: error => {
      console.error(error);

      // close the drawer and reset the form
      setOpen(false);
      form.reset();

      // show a toast message
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // call the mutation
    createWorkspace.mutateAsync({
      name: values.workspaceName,
      description: values.workspaceDesc,
      orgId: organizationId,
    });
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Create new Workspace</SheetTitle>
          <SheetDescription>
            Create a workspace for each client. Within each workspace, you can
            create multiple projects.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="workspaceName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspace name</FormLabel>
                  <FormControl>
                    <Input placeholder="Client 1" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the name for the workspace.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="workspaceDesc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspace description</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    Provide a short description of the workspace.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button type="submit" disabled={createWorkspace.isPending}>
                {createWorkspace.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
